import { villageStart } from "game/geography";
import SimplexNoise from "simplex-noise";

import { WorldGrid as Grid, WorldHex as Hex, ALL_SIDES } from "game/hex";
import { annualClimate } from "game/annual_moisture";
import { createLandField, surveyWorld } from "game/world_terrain";

// NOTE: The world is a hexagon of world hexes, and the map the game is
// played on is one of them. Categories summarise reaches for legacy/debug
// and hourly weather. The retained climate grid and continuous temperature
// field supply physical habitat through biome_field.js.

export const AXES = {
  morality: ["good", "neutral", "evil"],
  temperature: ["hot", "temperate", "cold"],
  moisture: ["wet", "normal", "dry"],
};

export const BIOMES = {
  desert: { temperature: "hot", moisture: "dry" },
  savanna: { temperature: "hot", moisture: "normal" },
  jungle: { temperature: "hot", moisture: "wet" },
  steppe: { temperature: "temperate", moisture: "dry" },
  grassland: { temperature: "temperate", moisture: "normal" },
  marsh: { temperature: "temperate", moisture: "wet" },
  tundra: { temperature: "cold", moisture: "dry" },
  taiga: { temperature: "cold", moisture: "normal" },
  snowfield: { temperature: "cold", moisture: "wet" },
};

export const biomeOf = (temperature, moisture) => Object.keys(BIOMES)
  .find(b => BIOMES[b].temperature == temperature && BIOMES[b].moisture == moisture);

export const WORLD_RADIUS = 5;
const WORLD_CENTRE = Hex(WORLD_RADIUS, WORLD_RADIUS);

// NOTE: Neighbours may differ by one step at most.
const pullTowards = (step, anchor) => Math.abs(step - anchor) <= 1 ? step : anchor + Math.sign(step - anchor);

const neighbourIndex = new Map();
const neighbourKeys = hex => {
  const key = hex.toString();
  if (!neighbourIndex.has(key)) {
    neighbourIndex.set(key, Grid.hexagon({ center: hex, radius: 1 })
      .neighborsOf(hex, ALL_SIDES).map(n => n.toString()));
  }
  return neighbourIndex.get(key);
};

const layouts = new Map();
export function generateRegions(seed, { radius = WORLD_RADIUS } = {}) {
  const centre = radius == WORLD_RADIUS ? WORLD_CENTRE : Hex(radius, radius);
  if (!layouts.has(radius)) {
    layouts.set(radius, [...Grid.spiral({ center: centre, radius })]);
  }
  // NOTE: Sample separate seeded fields in units of a world hex's height.
  // Temperature and morality retain their seeded fields. Moisture comes
  // from terrain-aware long-term transport after the dry-land survey.
  const fields = Object.fromEntries(["morality", "temperature"].map(axis => [axis, new SimplexNoise(axis + ":" + seed)]));
  const c = centre.toPoint();
  const hexes = layouts.get(radius).map(hex => {
    const values = {};
    const p = hex.toPoint();
    const x = (p.x - c.x) / Hex().height();
    const y = (p.y - c.y) / Hex().height();
    const latitude = radius ? Math.abs(y) / radius : 0;
    for (const axis of ["morality", "temperature"]) {
      const scale = axis == "temperature" ? 0.145 : 0.125;
      const threshold = axis == "temperature" ? 0.22 : 0.215;
      const noise = fields[axis].noise2D(x * scale + 17.3, y * scale - 9.1);
      const value = axis == "temperature" ? 1.6 * (latitude - 0.4) + 0.65 * noise : noise;
      values[axis] = AXES[axis][value < -threshold ? 0 : value > threshold ? 2 : 1];
    }
    return { x: hex.x, y: hex.y, ...values };
  });
  return { radius, centre: { x: centre.x, y: centre.y }, hexes };
}

export function generateWorld(seed, options = {}) {
  const regions = generateRegions(seed, options);
  let { hexes } = regions;
  const surveyed = surveyWorld(hexes, seed, villageStart);
  const climate = annualClimate(surveyed.hexes, createLandField(surveyed.landSeed), seed);
  hexes = climate.hexes.map(h => ({...h, moisture:h.annualMoisture > .68 ? "wet" : h.annualMoisture < .42 ? "dry" : "normal"}));
  repairRegions(hexes);
  const world = { ...regions, hexes };
  return { ...surveyed, ...world, prevailingWind:climate.wind, climate:{seed,moisture:climate.grid} };
}

export function repairRegions(hexes) {
  // NOTE: Use the same pull as an in-play change, in fixed spiral and side
  // order. A clash between extremes pulls one to the middle, which cannot
  // create another clash on a three-step axis. Include oceans in the repair.
  const byKey = new Map(hexes.map(h => [Hex(h).toString(), h]));
  for (const h of hexes) {
    for (const key of neighbourKeys(Hex(h))) {
      const n = byKey.get(key);
      if (!n) { continue; }
      for (const axis in AXES) {
        const steps = AXES[axis];
        n[axis] = steps[pullTowards(steps.indexOf(n[axis]), steps.indexOf(h[axis]))];
      }
    }
  }
  hexes.forEach(h => { h.biome = biomeOf(h.temperature, h.moisture); });
  return hexes;
}

export const homeOf = world => world.hexes.find(h => h.x == world.home.x && h.y == world.home.y);

export const colder = temperature =>
  AXES.temperature[Math.min(AXES.temperature.length - 1, AXES.temperature.indexOf(temperature) + 1)];

// NOTE: High ground is one step colder than its world hex, down to cold.
export const tileTemperature = (worldTemperature, high) => high ? colder(worldTemperature) : worldTemperature;
